import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { useSSE } from "../hooks/useSSE";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LifeBuoy, Plus, X, Send, AtSign, Eye, EyeOff, ChevronDown,
  AlertCircle, Clock, CheckCircle, XCircle, Filter,
  Paperclip, Image as ImageIcon, Trash2, RefreshCw,
  Tag, User, Building2, ArrowLeft, MoreVertical,
  MessageSquare, Lock, Unlock, ZoomIn, Megaphone,
  BellRing, CheckCheck, Edit2, CalendarDays,
  FileText, File, Download
} from "lucide-react";
import { toast } from "sonner";
import { AuthContext } from "../App";
import DateInput from "../components/ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Constantes ─────────────────────────────────────────────────────────────────
const PRIORIDADES = [
  { value: "baja",    label: "Baja",    cls: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
  { value: "media",   label: "Media",   cls: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  { value: "alta",    label: "Alta",    cls: "bg-orange-500/20 text-orange-300 border-orange-500/30" },
  { value: "urgente", label: "Urgente", cls: "bg-red-500/20 text-red-300 border-red-500/30" },
];
const CATEGORIAS = [
  { value: "hardware",  label: "Hardware" },
  { value: "software",  label: "Software" },
  { value: "red",       label: "Red" },
  { value: "acceso",    label: "Acceso" },
  { value: "otro",      label: "Otro" },
];
const ESTADOS = [
  { value: "abierto",     label: "Abierto",     icon: AlertCircle, cls: "text-amber-400 bg-amber-500/10 border-amber-500/20" },
  { value: "en_proceso",  label: "En proceso",  icon: Clock,        cls: "text-blue-400 bg-blue-500/10 border-blue-500/20" },
  { value: "resuelto",    label: "Resuelto",    icon: CheckCircle,  cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20" },
  { value: "cerrado",     label: "Cerrado",     icon: XCircle,      cls: "text-slate-400 bg-slate-500/10 border-slate-500/20" },
];
const TIPOS_ENTRADA = [
  { value: "comentario",    label: "Comentario",        icon: MessageSquare, soloSoporte: false },
  { value: "respuesta",     label: "Respuesta oficial", icon: Send,          soloSoporte: true },
  { value: "proceso",       label: "Proceso / pasos",   icon: Tag,           soloSoporte: true },
  { value: "nota_interna",  label: "Nota interna",      icon: Lock,          soloSoporte: true },
];

// ── Helpers ────────────────────────────────────────────────────────────────────
const prioMeta   = (v) => PRIORIDADES.find(p => p.value === v) || PRIORIDADES[1];
const estadoMeta = (v) => ESTADOS.find(e => e.value === v) || ESTADOS[0];
const fmtFecha = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-PY", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso.slice(0, 16).replace("T", " "); }
};
const fmtFechaCorta = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-PY", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso.slice(0, 10); }
};

const fileToDataUri = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// ── Badges ─────────────────────────────────────────────────────────────────────
const PrioBadge = ({ value }) => {
  const m = prioMeta(value);
  return <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-semibold ${m.cls}`}>{m.label}</span>;
};
const EstadoBadge = ({ value }) => {
  const m = estadoMeta(value);
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${m.cls}`}>
      <Icon className="w-3 h-3" />{m.label}
    </span>
  );
};

// ── Lightbox ───────────────────────────────────────────────────────────────────
function Lightbox({ src, onClose }) {
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4">
      <img src={src} alt="Imagen adjunta" className="max-w-full max-h-full object-contain rounded-xl shadow-2xl" onClick={e => e.stopPropagation()} />
      <button onClick={onClose} className="absolute top-4 right-4 text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-all">
        <X className="w-5 h-5" />
      </button>
    </div>
  );
}

// ── Helpers de archivos ────────────────────────────────────────────────────────
const ACCEPT_FILES = "image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv,application/zip,application/x-rar-compressed,application/vnd.rar,application/x-7z-compressed,application/octet-stream";
const ARCHIVE_EXTS = [".rar", ".zip", ".7z", ".tar", ".gz"];
const isArchive = (ct, nombre) => {
  if (!ct && !nombre) return false;
  if (["application/zip","application/x-zip-compressed","application/x-rar-compressed",
       "application/vnd.rar","application/x-7z-compressed","application/x-tar",
       "application/gzip","application/x-gzip"].includes(ct)) return true;
  const ext = nombre ? "." + nombre.split(".").pop().toLowerCase() : "";
  return ARCHIVE_EXTS.includes(ext);
};

function fileIconCls(ct, nombre) {
  if (!ct) return "text-slate-400";
  if (ct === "application/pdf") return "text-red-400";
  if (ct.includes("word")) return "text-blue-400";
  if (ct.includes("excel") || ct.includes("spreadsheet")) return "text-emerald-400";
  if (ct.startsWith("text/")) return "text-slate-300";
  if (isArchive(ct, nombre)) return "text-amber-400";
  return "text-slate-400";
}
function FileIcon({ contentType, nombre, className = "w-4 h-4" }) {
  if (!contentType) return <File className={`${className} text-slate-400`} />;
  if (contentType === "application/pdf") return <FileText className={`${className} text-red-400`} />;
  if (contentType.includes("word")) return <FileText className={`${className} text-blue-400`} />;
  if (contentType.includes("excel") || contentType.includes("spreadsheet")) return <FileText className={`${className} text-emerald-400`} />;
  if (contentType.startsWith("text/")) return <FileText className={`${className} text-slate-300`} />;
  if (isArchive(contentType, nombre)) return <File className={`${className} text-amber-400`} />;
  return <File className={`${className} text-slate-400`} />;
}

// Adjunto de ticket: los nuevos se guardan en GridFS (file_id, se descargan con sesión);
// los antiguos traen el contenido embebido (data_uri).
function ArchivoLink({ a }) {
  const { token } = useContext(AuthContext);
  const [bajando, setBajando] = useState(false);
  const cls = "flex items-center gap-1.5 bg-white/5 border border-white/10 hover:border-white/25 rounded-lg px-2.5 py-1.5 text-xs text-slate-300 hover:text-white transition-all max-w-[220px]";
  const inner = (
    <>
      <FileIcon contentType={a.content_type} nombre={a.nombre} className="w-3.5 h-3.5 flex-shrink-0" />
      <span className="truncate">{a.nombre}</span>
      <Download className="w-3 h-3 flex-shrink-0 text-slate-500" />
    </>
  );
  if (!a.file_id) return <a href={a.data_uri} download={a.nombre} className={cls} title={`Descargar ${a.nombre}`}>{inner}</a>;
  const bajar = async () => {
    setBajando(true);
    try {
      const res = await fetch(`${API}/admin/soporte/archivos/${a.file_id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("No se pudo descargar");
      const url = URL.createObjectURL(await res.blob());
      const l = document.createElement("a");
      l.href = url; l.download = a.nombre; document.body.appendChild(l); l.click(); l.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(e.message); }
    setBajando(false);
  };
  return <button type="button" onClick={bajar} disabled={bajando} className={cls} title={`Descargar ${a.nombre}`}>{inner}</button>;
}

// ── Selector imágenes + archivos ───────────────────────────────────────────────
function ImagenAdjunta({ imagenes, setImagenes, archivos = [], setArchivos = () => {}, disabled }) {
  const imgRef  = useRef(null);
  const fileRef = useRef(null);
  const { token } = useContext(AuthContext);
  const [subiendo, setSubiendo] = useState(0);

  const handleImageFiles = async (files) => {
    const validos = Array.from(files).filter(f => f.type.startsWith("image/") && f.size <= 5 * 1024 * 1024);
    if (validos.length < files.length) toast.warning("Solo imágenes menores a 5 MB");
    const uris = await Promise.all(validos.map(fileToDataUri));
    setImagenes(prev => [...prev, ...uris]);
  };

  const handleArchivosFiles = async (files) => {
    const MAX = 100 * 1024 * 1024;
    const validos = Array.from(files).filter(f => {
      const ok = !f.type.startsWith("image/") && f.size <= MAX;
      if (!ok && f.size > MAX) toast.warning(`${f.name} supera 100 MB`);
      return ok;
    });
    setSubiendo(n => n + validos.length);
    const nuevos = [];
    for (const f of validos) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const res = await fetch(`${API}/admin/soporte/archivos`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "No se pudo subir");
        nuevos.push(data);
      } catch (e) {
        toast.error(`${f.name}: ${e.message || "error al subir"}`);
      }
      setSubiendo(n => n - 1);
    }
    setArchivos(prev => [...prev, ...nuevos]);
  };

  return (
    <div className="space-y-2">
      {/* Imágenes */}
      <div className="flex flex-wrap gap-2">
        {imagenes.map((uri, i) => (
          <div key={i} className="relative group">
            <img src={uri} alt="adjunto" className="w-16 h-16 object-cover rounded-lg border border-white/10" />
            {!disabled && (
              <button type="button" onClick={() => setImagenes(prev => prev.filter((_, j) => j !== i))}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                <X className="w-3 h-3 text-white" />
              </button>
            )}
          </div>
        ))}
      </div>

      {subiendo > 0 && <p className="text-xs text-amber-300">Subiendo {subiendo} archivo(s)…</p>}
      {/* Archivos (no imagen) */}
      {archivos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {archivos.map((a, i) => (
            <div key={i} className="relative group flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-slate-300 max-w-[200px]">
              <FileIcon contentType={a.content_type} nombre={a.nombre} className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{a.nombre}</span>
              {!disabled && (
                <button type="button" onClick={() => setArchivos(prev => prev.filter((_, j) => j !== i))}
                  className="ml-1 text-slate-600 hover:text-red-400 transition-all flex-shrink-0">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Botones agregar */}
      {!disabled && (
        <div className="flex gap-2">
          <button type="button" onClick={() => imgRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-white/20 rounded-lg text-slate-500 hover:text-slate-300 hover:border-white/40 transition-all text-xs">
            <ImageIcon className="w-3.5 h-3.5" /> Imagen
          </button>
          <button type="button" onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-white/20 rounded-lg text-slate-500 hover:text-slate-300 hover:border-white/40 transition-all text-xs">
            <Paperclip className="w-3.5 h-3.5" /> Archivo
          </button>
        </div>
      )}
      <input ref={imgRef}  type="file" accept="image/*" multiple className="hidden" onChange={e => handleImageFiles(e.target.files)} />
      <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.rar,.zip,.7z,.tar,.gz" multiple className="hidden" onChange={e => handleArchivosFiles(e.target.files)} />
    </div>
  );
}

// ── Timeline entrada ──────────────────────────────────────────────────────────
function EntradaTimeline({ entrada, esSoporte, onToggleVisibilidad, currentUserId }) {
  const [lightbox, setLightbox] = useState(null);
  const esInterno    = entrada.tipo === "nota_interna";
  const esProceso    = entrada.tipo === "proceso";
  const esRespuesta  = entrada.tipo === "respuesta";
  const esCreacion   = entrada.tipo === "creacion";
  const esCambioEst  = entrada.tipo === "cambio_estado";

  const borderCls = esInterno ? "border-l-2 border-red-500/40 bg-red-500/5"
    : esProceso    ? "border-l-2 border-cyan-500/40 bg-cyan-500/5"
    : esRespuesta  ? "border-l-2 border-emerald-500/40 bg-emerald-500/5"
    : esCreacion   ? "border-l-2 border-arandu-blue/40 bg-arandu-blue/5"
    : esCambioEst  ? "border-l-2 border-slate-500/40 bg-slate-500/5"
    : "border-l-2 border-white/10 bg-white/2";

  const tipoLabel = esCreacion   ? "Reporte inicial"
    : esInterno    ? "🔒 Nota interna"
    : esProceso    ? "🔧 Proceso de resolución"
    : esRespuesta  ? "💬 Respuesta oficial"
    : esCambioEst  ? "📋 Cambio de estado"
    : "Comentario";

  return (
    <>
      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}
      <div className={`rounded-xl px-4 py-3 ${borderCls}`}>
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white text-xs font-semibold">{entrada.autor_nombre}</span>
            <span className="text-slate-500 text-[10px] capitalize bg-white/5 px-1.5 py-0.5 rounded">{tipoLabel}</span>
            {esSoporte && !esCreacion && !esCambioEst && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded border flex items-center gap-0.5 ${
                entrada.visible_usuario
                  ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/20"
                  : "text-slate-500 bg-white/5 border-white/10"
              }`}>
                {entrada.visible_usuario ? <><Eye className="w-2.5 h-2.5" /> Visible</> : <><EyeOff className="w-2.5 h-2.5" /> Interno</>}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-slate-500 text-[10px]">{fmtFecha(entrada.fecha)}</span>
            {esSoporte && !esCreacion && !esCambioEst && (
              <button onClick={() => onToggleVisibilidad(entrada.id, !entrada.visible_usuario)}
                title={entrada.visible_usuario ? "Ocultar al usuario" : "Mostrar al usuario"}
                className={`p-1 rounded transition-all ${entrada.visible_usuario ? "text-emerald-400 hover:text-emerald-300" : "text-slate-500 hover:text-white"}`}>
                {entrada.visible_usuario ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              </button>
            )}
          </div>
        </div>
        <p className="text-slate-300 text-sm whitespace-pre-wrap leading-relaxed">{entrada.contenido}</p>
        {(entrada.menciones || []).length > 0 && (
          <p className="mt-1 text-[10px] text-arandu-blue flex items-center gap-1"><AtSign className="w-3 h-3" />Mencionó a {entrada.menciones.length} {entrada.menciones.length === 1 ? "persona" : "personas"}</p>
        )}
        {(entrada.imagenes || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {entrada.imagenes.map((uri, i) => (
              <button key={i} onClick={() => setLightbox(uri)} className="relative group">
                <img src={uri} alt="adjunto" className="w-20 h-20 object-cover rounded-lg border border-white/10 hover:border-white/30 transition-all" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 rounded-lg flex items-center justify-center transition-all">
                  <ZoomIn className="w-5 h-5 text-white" />
                </div>
              </button>
            ))}
          </div>
        )}
        {(entrada.archivos || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {entrada.archivos.map((a, i) => (
              <ArchivoLink key={i} a={a} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ── Tarjeta de aviso ──────────────────────────────────────────────────────────
function AvisoCard({ aviso, esSoporte, empresasPropias, onLeer, onEditar, onEliminar }) {
  const [lightbox, setLightbox] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const empresaNombres = aviso.empresa_ids?.includes("all")
    ? ["Todas las empresas"]
    : (aviso.empresa_ids || []).map(id => {
        const ep = empresasPropias.find(e => String(e.id) === String(id));
        return ep?.nombre || id;
      });

  return (
    <>
      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}
      <div className={`rounded-xl border transition-all ${
        aviso.leido
          ? "border-white/5 bg-white/2"
          : "border-arandu-blue/30 bg-arandu-blue/5"
      }`}>
        <div className="p-4">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex items-center gap-2 min-w-0">
              {!aviso.leido && <div className="w-2 h-2 rounded-full bg-arandu-blue flex-shrink-0 mt-0.5" />}
              <h3 className={`font-semibold text-sm truncate ${aviso.leido ? "text-slate-300" : "text-white"}`}>
                {aviso.titulo}
              </h3>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {!aviso.leido && (
                <button onClick={() => onLeer(aviso.id)}
                  title="Marcar como leído"
                  className="p-1.5 text-arandu-blue hover:text-white transition-all rounded-lg hover:bg-white/5">
                  <CheckCheck className="w-4 h-4" />
                </button>
              )}
              {esSoporte && (
                <>
                  <button onClick={() => onEditar(aviso)} title="Editar"
                    className="p-1.5 text-slate-500 hover:text-white transition-all rounded-lg hover:bg-white/5">
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => onEliminar(aviso.id)} title="Eliminar"
                    className="p-1.5 text-slate-500 hover:text-red-400 transition-all rounded-lg hover:bg-white/5">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>

          <div className={`text-slate-400 text-sm leading-relaxed ${expanded ? "" : "line-clamp-3"}`}>
            {aviso.contenido}
          </div>
          {aviso.contenido?.length > 120 && (
            <button onClick={() => setExpanded(v => !v)} className="text-arandu-blue text-xs mt-1 hover:underline">
              {expanded ? "Ver menos" : "Ver más"}
            </button>
          )}

          {(aviso.imagenes || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {aviso.imagenes.map((uri, i) => (
                <button key={i} onClick={() => setLightbox(uri)} className="relative group">
                  <img src={uri} alt="adjunto" className="w-16 h-16 object-cover rounded-lg border border-white/10 hover:border-white/30 transition-all" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 rounded-lg flex items-center justify-center transition-all">
                    <ZoomIn className="w-4 h-4 text-white" />
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-white/5 flex-wrap">
            <span className="text-slate-500 text-[10px] flex items-center gap-1">
              <CalendarDays className="w-3 h-3" />{fmtFechaCorta(aviso.fecha_creacion)}
            </span>
            <span className="text-slate-500 text-[10px] flex items-center gap-1">
              <User className="w-3 h-3" />{aviso.creado_por_nombre}
            </span>
            {empresaNombres.map((n, i) => (
              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-300 border border-violet-500/20">
                {n}
              </span>
            ))}
            {aviso.fecha_expiracion && (
              <span className="text-[10px] text-amber-400/70 flex items-center gap-1">
                Expira: {fmtFechaCorta(aviso.fecha_expiracion)}
              </span>
            )}
            {aviso.leido && (
              <span className="text-[10px] text-slate-600 flex items-center gap-1 ml-auto">
                <CheckCheck className="w-3 h-3" /> Leído
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );
}


// ── MAIN PAGE ─────────────────────────────────────────────────────────────────
export default function SoportePage() {
  const { user, token, hasPermission, activeEmpresaPropia } = useContext(AuthContext);
  const hdrs = { Authorization: `Bearer ${token}` };
  const [searchParams] = useSearchParams();

  const esSoporte      = ["admin", "super_admin", "gerente"].includes(user?.role);
  const puedeVer       = esSoporte || hasPermission("soporte.ver") || hasPermission("soporte.crear");
  const puedeCrear     = esSoporte || hasPermission("soporte.crear");
  const puedeResponder = esSoporte || hasPermission("soporte.responder");
  const puedeGestionar = esSoporte || hasPermission("soporte.gestionar");
  const puedeEliminar  = esSoporte || hasPermission("soporte.eliminar");
  const puedeAvisos    = esSoporte || hasPermission("avisos.ver");
  const puedeCrearAviso= esSoporte;  // solo admin/gerente por rol, sin permiso granular

  // ── State ─────────────────────────────────────────────────────────────────
  const [tab, setTab] = useState("tickets");  // "tickets" | "avisos"

  // Tickets
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ticketDetalle, setTicketDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [filtrosEstado, setFiltrosEstado] = useState(new Set()); // Set vacío = todos
  const [filtroPrio, setFiltroPrio] = useState("");
  const [soloNoLeidos, setSoloNoLeidos] = useState(false);
  const [dropdownEstadoOpen, setDropdownEstadoOpen] = useState(false);
  const dropdownEstadoRef = useRef(null);
  const [busqueda, setBusqueda] = useState("");

  // Nuevo ticket
  const [showForm, setShowForm] = useState(false);
  const [nuevoForm, setNuevoForm] = useState({ titulo: "", descripcion: "", prioridad: "media", categoria: "otro", empresa_id: "", empresa_nombre: "" });
  const [nuevoImagenes, setNuevoImagenes] = useState([]);
  const [nuevoArchivos, setNuevoArchivos] = useState([]);
  const [savingNuevo, setSavingNuevo] = useState(false);
  // visible_para: selección de visibilidad
  const [visibleParaActivo, setVisibleParaActivo] = useState(false);
  const [visibleParaTodos, setVisibleParaTodos] = useState(true);
  const [visibleParaIds, setVisibleParaIds] = useState([]); // IDs seleccionados si no es "todos"
  const [empresaUsuarios, setEmpresaUsuarios] = useState([]); // usuarios de la empresa seleccionada

  // Respuesta
  const [respForm, setRespForm] = useState({ contenido: "", tipo: "comentario", visible_usuario: false });
  // Menciones (@usuario) en el comentario/respuesta
  const [mencionables, setMencionables] = useState([]);
  const [respMenciones, setRespMenciones] = useState([]);      // [{id, name}]
  const [mencionQuery, setMencionQuery] = useState(null);       // null = lista cerrada; string = filtro tras "@"
  const [respImagenes, setRespImagenes] = useState([]);
  const [respArchivos, setRespArchivos] = useState([]);
  const [savingResp, setSavingResp] = useState(false);
  const [savingEstado, setSavingEstado] = useState(false);
  const [savingPrioridad, setSavingPrioridad] = useState(false);

  // Empresas propias (para selector en form y avisos)
  const [empresasPropias, setEmpresasPropias] = useState([]);

  // Avisos
  const [avisos, setAvisos] = useState([]);
  const [loadingAvisos, setLoadingAvisos] = useState(false);
  const [showAvisoForm, setShowAvisoForm] = useState(false);
  const [editingAviso, setEditingAviso] = useState(null);
  const [avisoForm, setAvisoForm] = useState({ titulo: "", contenido: "", empresa_ids: ["all"], fecha_expiracion: "", imagenes: [] });
  const [avisoImagenes, setAvisoImagenes] = useState([]);
  const [savingAviso, setSavingAviso] = useState(false);

  // Lightbox global
  const [lightbox, setLightbox] = useState(null);

  // ── Empresas del usuario actual ────────────────────────────────────────────
  // Para usuarios normales: solo sus logos_asignados
  const misEmpresas = esSoporte
    ? empresasPropias
    : empresasPropias.filter(ep =>
        (user?.logos_asignados || []).map(String).includes(String(ep.id))
      );

  // ── Cerrar dropdown al clickear afuera ───────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (dropdownEstadoRef.current && !dropdownEstadoRef.current.contains(e.target)) {
        setDropdownEstadoOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // Estado se filtra client-side para multi-select; solo prioridad va al server
      if (filtroPrio) params.set("prioridad", filtroPrio);
      const url = `${API}/admin/soporte${params.toString() ? "?" + params.toString() : ""}`;
      const r = await fetch(url, { headers: hdrs });
      if (r.ok) setTickets(await r.json());
    } catch { toast.error("Error al cargar tickets"); }
    setLoading(false);
  }, [filtroPrio, token]); // eslint-disable-line

  const toggleFiltroEstado = (valor) => {
    setFiltrosEstado(prev => {
      const next = new Set(prev);
      if (next.has(valor)) next.delete(valor);
      else next.add(valor);
      return next;
    });
  };

  const fetchAvisos = useCallback(async () => {
    setLoadingAvisos(true);
    try {
      const r = await fetch(`${API}/admin/avisos`, { headers: hdrs });
      if (r.ok) setAvisos(await r.json());
    } catch { }
    setLoadingAvisos(false);
  }, [token]); // eslint-disable-line

  const fetchEmpresasPropias = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/empresas-propias`, { headers: hdrs });
      if (r.ok) setEmpresasPropias(await r.json());
    } catch { }
  }, [token]); // eslint-disable-line

  const fetchEmpresaUsuarios = useCallback(async (empresaId) => {
    if (!empresaId) { setEmpresaUsuarios([]); return; }
    try {
      const r = await fetch(`${API}/admin/soporte/empresa/${empresaId}/usuarios`, { headers: hdrs });
      if (r.ok) setEmpresaUsuarios(await r.json());
      else setEmpresaUsuarios([]);
    } catch { setEmpresaUsuarios([]); }
  }, [token]); // eslint-disable-line

  const fetchDetalle = async (id) => {
    setLoadingDetalle(true);
    try {
      const r = await fetch(`${API}/admin/soporte/${id}`, { headers: hdrs });
      if (r.ok) {
        setTicketDetalle(await r.json());
        setRespForm({ contenido: "", tipo: "comentario", visible_usuario: false });
        setRespImagenes([]);
        setRespArchivos([]);
        setRespMenciones([]); setMencionQuery(null);
        fetch(`${API}/admin/soporte/${id}/mencionables`, { headers: hdrs }).then(x => x.ok ? x.json() : []).then(setMencionables).catch(() => setMencionables([]));
        // Marcar como visto (persiste en servidor antes de cualquier re-render)
        await fetch(`${API}/admin/soporte/${id}/vista`, { method: "POST", headers: hdrs }).catch(() => {});
        // Actualizar el badge en la lista
        setTickets(prev => prev.map(t => t.id === id ? { ...t, tiene_novedades: false } : t));
      }
    } catch { toast.error("Error al cargar ticket"); }
    setLoadingDetalle(false);
  };

  // ── SSE — eventos en tiempo real ─────────────────────────────────────────
  useSSE({
    logoTipo: activeEmpresaPropia?.slug,
    token,
    enabled: !!activeEmpresaPropia?.slug && !!token,
    onEvent: useCallback((event) => {
      if (event.tipo === "ticket_nuevo") {
        fetchTickets();
      } else if (event.tipo === "ticket_actualizado") {
        fetchTickets();
        setTickets(prev => prev.map(t =>
          t.id === event.data.id
            ? { ...t, estado: event.data.estado ?? t.estado, historial_count: event.data.historial_count ?? t.historial_count, tiene_novedades: true }
            : t
        ));
        if ((event.data?.mencionados || []).includes(user?.id)) {
          toast.info(`${event.data.autor_nombre || "Alguien"} te mencionó en ${event.data.numero || "un ticket"}: ${event.data.titulo || ""}`, { duration: 8000 });
        }
        if (ticketDetalle?.id === event.data?.id) fetchDetalle(event.data.id);
      }
    }, [fetchTickets, ticketDetalle?.id, user?.id]), // eslint-disable-line
  });

  useEffect(() => { fetchTickets(); }, [fetchTickets]);
  useEffect(() => { if (puedeAvisos) fetchAvisos(); }, [fetchAvisos, puedeAvisos]);
  useEffect(() => { fetchEmpresasPropias(); }, [fetchEmpresasPropias]);

  // Abrir ticket o pestaña directamente desde URL params (?ticket=ID o ?tab=avisos o ?nuevo=ticket)
  useEffect(() => {
    const ticketId = searchParams.get("ticket");
    const tabParam  = searchParams.get("tab");
    const nuevoParam = searchParams.get("nuevo");
    if (ticketId) {
      fetchDetalle(ticketId);
    } else if (tabParam === "avisos") {
      setTab("avisos");
    } else if (nuevoParam === "ticket") {
      setShowForm(true);
    }
  }, []); // eslint-disable-line

  // ── Acciones tickets ──────────────────────────────────────────────────────
  const handleCrear = async (e) => {
    e.preventDefault();
    if (!nuevoForm.titulo.trim() || !nuevoForm.descripcion.trim()) {
      toast.error("Título y descripción son obligatorios");
      return;
    }
    if (!nuevoForm.empresa_id) {
      toast.error("Seleccioná la empresa a la que pertenece el ticket");
      return;
    }
    setSavingNuevo(true);
    try {
      const r = await fetch(`${API}/admin/soporte`, {
        method: "POST",
        headers: { ...hdrs, "Content-Type": "application/json" },
        body: JSON.stringify({
          ...nuevoForm,
          imagenes: nuevoImagenes,
          archivos: nuevoArchivos,
          visible_para: visibleParaActivo ? (visibleParaTodos ? ["all"] : visibleParaIds) : [],
          logo_tipo: activeEmpresaPropia?.slug || "",
        }),
      });
      if (r.ok) {
        const t = await r.json();
        toast.success(`Ticket ${t.numero} creado`);
        setShowForm(false);
        setNuevoForm({ titulo: "", descripcion: "", prioridad: "media", categoria: "otro", empresa_id: "", empresa_nombre: "" });
        setNuevoImagenes([]);
        setNuevoArchivos([]);
        setVisibleParaActivo(false);
        setVisibleParaTodos(true);
        setVisibleParaIds([]);
        setEmpresaUsuarios([]);
        await fetchTickets();
        await fetchDetalle(t.id);
      } else {
        const err = await r.json();
        toast.error(err.detail || "Error al crear ticket");
      }
    } catch { toast.error("Error de conexión"); }
    setSavingNuevo(false);
  };

  const handleResponder = async (e) => {
    e.preventDefault();
    if (!respForm.contenido.trim()) { toast.error("El contenido es obligatorio"); return; }
    setSavingResp(true);
    try {
      const r = await fetch(`${API}/admin/soporte/${ticketDetalle.id}/historial`, {
        method: "POST",
        headers: { ...hdrs, "Content-Type": "application/json" },
        body: JSON.stringify({
          ...respForm, imagenes: respImagenes, archivos: respArchivos,
          // solo las menciones cuyo "@Nombre" sigue en el texto
          menciones: respMenciones.filter(m => respForm.contenido.includes(`@${m.name}`)).map(m => m.id),
        }),
      });
      if (r.ok) {
        setTicketDetalle(await r.json());
        setRespForm({ contenido: "", tipo: "comentario", visible_usuario: false });
        setRespImagenes([]);
        setRespArchivos([]);
        setRespMenciones([]); setMencionQuery(null);
        fetchTickets();
      } else {
        const err = await r.json();
        toast.error(err.detail || "Error al enviar");
      }
    } catch { toast.error("Error de conexión"); }
    setSavingResp(false);
  };

  const handleCambiarEstado = async (nuevoEstado) => {
    if (!ticketDetalle) return;
    setSavingEstado(true);
    try {
      const r = await fetch(`${API}/admin/soporte/${ticketDetalle.id}`, {
        method: "PUT",
        headers: { ...hdrs, "Content-Type": "application/json" },
        body: JSON.stringify({ estado: nuevoEstado }),
      });
      if (r.ok) { setTicketDetalle(await r.json()); fetchTickets(); }
    } catch { toast.error("Error al cambiar estado"); }
    setSavingEstado(false);
  };

  const handleCambiarPrioridad = async (nuevaPrioridad) => {
    if (!ticketDetalle || nuevaPrioridad === ticketDetalle.prioridad) return;
    setSavingPrioridad(true);
    try {
      const r = await fetch(`${API}/admin/soporte/${ticketDetalle.id}`, {
        method: "PUT",
        headers: { ...hdrs, "Content-Type": "application/json" },
        body: JSON.stringify({ prioridad: nuevaPrioridad }),
      });
      if (r.ok) { setTicketDetalle(await r.json()); fetchTickets(); }
      else toast.error("Error al cambiar prioridad");
    } catch { toast.error("Error al cambiar prioridad"); }
    setSavingPrioridad(false);
  };

  const handleToggleVisibilidad = async (entradaId, visible) => {
    if (!ticketDetalle) return;
    try {
      const r = await fetch(`${API}/admin/soporte/${ticketDetalle.id}/historial/${entradaId}/visibilidad`, {
        method: "PATCH",
        headers: { ...hdrs, "Content-Type": "application/json" },
        body: JSON.stringify({ visible_usuario: visible }),
      });
      if (r.ok) setTicketDetalle(await r.json());
    } catch { toast.error("Error al cambiar visibilidad"); }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm("¿Eliminar este ticket? Esta acción no se puede deshacer.")) return;
    try {
      const r = await fetch(`${API}/admin/soporte/${id}`, { method: "DELETE", headers: hdrs });
      if (r.ok) {
        toast.success("Ticket eliminado");
        if (ticketDetalle?.id === id) setTicketDetalle(null);
        fetchTickets();
      }
    } catch { toast.error("Error al eliminar"); }
  };

  // ── Acciones avisos ──────────────────────────────────────────────────────
  const handleLeerAviso = async (id) => {
    try {
      const r = await fetch(`${API}/admin/avisos/${id}/leer`, { method: "POST", headers: hdrs });
      if (r.ok) setAvisos(prev => prev.map(a => a.id === id ? { ...a, leido: true } : a));
    } catch { }
  };

  const handleGuardarAviso = async (e) => {
    e.preventDefault();
    if (!avisoForm.titulo.trim() || !avisoForm.contenido.trim()) {
      toast.error("Título y contenido son obligatorios");
      return;
    }
    setSavingAviso(true);
    try {
      const payload = { ...avisoForm, imagenes: avisoImagenes };
      const url = editingAviso ? `${API}/admin/avisos/${editingAviso.id}` : `${API}/admin/avisos`;
      const method = editingAviso ? "PUT" : "POST";
      const r = await fetch(url, {
        method,
        headers: { ...hdrs, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        toast.success(editingAviso ? "Aviso actualizado" : "Aviso publicado");
        setShowAvisoForm(false);
        setEditingAviso(null);
        setAvisoForm({ titulo: "", contenido: "", empresa_ids: ["all"], fecha_expiracion: "", imagenes: [] });
        setAvisoImagenes([]);
        fetchAvisos();
      } else {
        const err = await r.json();
        toast.error(err.detail || "Error al guardar aviso");
      }
    } catch { toast.error("Error de conexión"); }
    setSavingAviso(false);
  };

  const handleEditarAviso = (aviso) => {
    setEditingAviso(aviso);
    setAvisoForm({
      titulo: aviso.titulo,
      contenido: aviso.contenido,
      empresa_ids: aviso.empresa_ids || ["all"],
      fecha_expiracion: aviso.fecha_expiracion || "",
      imagenes: [],
    });
    setAvisoImagenes(aviso.imagenes || []);
    setShowAvisoForm(true);
  };

  const handleEliminarAviso = async (id) => {
    if (!window.confirm("¿Eliminar este aviso?")) return;
    try {
      const r = await fetch(`${API}/admin/avisos/${id}`, { method: "DELETE", headers: hdrs });
      if (r.ok) { toast.success("Aviso eliminado"); fetchAvisos(); }
    } catch { toast.error("Error al eliminar"); }
  };

  const toggleEmpresaAviso = (id) => {
    setAvisoForm(prev => {
      const current = prev.empresa_ids || [];
      if (id === "all") return { ...prev, empresa_ids: ["all"] };
      const sinAll = current.filter(x => x !== "all");
      return {
        ...prev,
        empresa_ids: sinAll.includes(id) ? sinAll.filter(x => x !== id) : [...sinAll, id],
      };
    });
  };

  // ── Filtro local ──────────────────────────────────────────────────────────
  const ticketsFiltrados = tickets.filter(t => {
    if (filtrosEstado.size > 0 && !filtrosEstado.has(t.estado)) return false;
    if (soloNoLeidos && !t.tiene_novedades) return false;
    if (!busqueda.trim()) return true;
    const q = busqueda.toLowerCase();
    return (t.numero || "").toLowerCase().includes(q)
      || (t.titulo || "").toLowerCase().includes(q)
      || (t.creado_por_nombre || "").toLowerCase().includes(q)
      || (t.empresa_nombre || "").toLowerCase().includes(q);
  });

  const labelEstadoDropdown = filtrosEstado.size === 0
    ? "Todos"
    : filtrosEstado.size === 1
      ? ESTADOS.find(e => filtrosEstado.has(e.value))?.label || "1 estado"
      : `${filtrosEstado.size} estados`;

  const novedadesCount = tickets.filter(t => t.tiene_novedades).length;

  // Selección múltiple para marcar como no leídos
  const [seleccion, setSeleccion] = useState(() => new Set());
  const toggleSel = (id) => setSeleccion(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const marcarNoLeidos = async () => {
    const ids = [...seleccion];
    if (!ids.length) return;
    try {
      const r = await fetch(`${API}/admin/soporte/marcar-no-leidos`, { method: "POST", headers: { ...hdrs, "Content-Type": "application/json" }, body: JSON.stringify({ ids }) });
      if (r.ok) {
        setTickets(prev => prev.map(t => seleccion.has(t.id) ? { ...t, tiene_novedades: true, marcado_no_leido: true } : t));
        toast.success(`${ids.length} ticket(s) marcados como no leídos`);
        setSeleccion(new Set());
      } else toast.error("No se pudo marcar como no leído");
    } catch { toast.error("Error de conexión"); }
  };

  const marcarTodosLeidos = async () => {
    try {
      const r = await fetch(`${API}/admin/soporte/marcar-todas-vistas`, { method: "POST", headers: hdrs });
      if (r.ok) {
        const d = await r.json();
        setTickets(prev => prev.map(t => ({ ...t, tiene_novedades: false, mencion_nueva: false })));
        toast.success(`${d.marcados} ticket(s) marcados como leídos`);
      } else toast.error("No se pudo marcar como leídos");
    } catch { toast.error("Error de conexión"); }
  };

  const avisosNoLeidos = avisos.filter(a => !a.leido).length;

  const hayModalAbierto = !!(showForm || showAvisoForm || lightbox);
  useAutoRefresh(fetchTickets, 30_000, !hayModalAbierto);

  // ── Render ────────────────────────────────────────────────────────────────
  if (!puedeVer) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <div className="text-center">
          <LifeBuoy className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Sin acceso al módulo de Soporte</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}

      <div className="flex h-screen bg-arandu-dark overflow-hidden">

        {/* ── Panel izquierdo ── */}
        <div className={`flex flex-col h-full border-r border-white/5 bg-arandu-dark-light transition-all overflow-hidden ${ticketDetalle ? "w-0 lg:w-96" : "w-full lg:w-96"}`}>

          {/* Header */}
          <div className="sticky top-0 z-10 bg-arandu-dark-light/95 backdrop-blur-sm border-b border-white/5 p-4">
            {/* Título + volver */}
            <div className="flex items-center gap-2 mb-3">
              <Link to="/sistema" className="p-1.5 text-slate-500 hover:text-white transition-all rounded-lg hover:bg-white/5">
                <ArrowLeft className="w-4 h-4" />
              </Link>
              <h1 className="font-heading text-lg font-bold text-white flex items-center gap-2">
                <LifeBuoy className="w-5 h-5 text-arandu-blue" />
                Soporte
              </h1>
              <div className="ml-auto flex items-center gap-1.5">
                {tab === "tickets" && puedeCrear && (
                  <button onClick={() => setShowForm(true)}
                    className="flex items-center gap-1.5 bg-arandu-blue hover:bg-arandu-blue/80 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-all">
                    <Plus className="w-4 h-4" /> Nuevo
                  </button>
                )}
                {tab === "avisos" && puedeCrearAviso && (
                  <button onClick={() => { setEditingAviso(null); setAvisoForm({ titulo: "", contenido: "", empresa_ids: ["all"], fecha_expiracion: "", imagenes: [] }); setAvisoImagenes([]); setShowAvisoForm(true); }}
                    className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-500 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-all">
                    <Plus className="w-4 h-4" /> Aviso
                  </button>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-arandu-dark rounded-lg p-1 mb-3">
              <button
                onClick={() => setTab("tickets")}
                className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                  tab === "tickets" ? "bg-arandu-dark-light text-white shadow" : "text-slate-400 hover:text-white"
                }`}
              >
                <LifeBuoy className="w-3.5 h-3.5" />
                Tickets
                {novedadesCount > 0 && (
                  <span className="bg-arandu-blue text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">
                    {novedadesCount}
                  </span>
                )}
              </button>
              {puedeAvisos && (
                <button
                  onClick={() => setTab("avisos")}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                    tab === "avisos" ? "bg-arandu-dark-light text-white shadow" : "text-slate-400 hover:text-white"
                  }`}
                >
                  <Megaphone className="w-3.5 h-3.5" />
                  Avisos
                  {avisosNoLeidos > 0 && (
                    <span className="bg-violet-500 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">
                      {avisosNoLeidos}
                    </span>
                  )}
                </button>
              )}
            </div>

            {/* Filtros (solo en tab tickets) */}
            {tab === "tickets" && (
              <>
                <input type="text" value={busqueda} onChange={e => setBusqueda(e.target.value)}
                  placeholder="Buscar ticket..." className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue mb-2" />

                <div className="flex gap-1.5 flex-wrap">

                  {/* ── Multi-select estados ── */}
                  <div className="relative flex-1 min-w-[110px]" ref={dropdownEstadoRef}>
                    <button
                      onClick={() => setDropdownEstadoOpen(v => !v)}
                      className={`w-full flex items-center justify-between gap-1 bg-arandu-dark border rounded-lg px-2 py-1.5 text-xs transition-all
                        ${filtrosEstado.size > 0 ? "border-arandu-blue text-arandu-blue" : "border-white/10 text-slate-400 hover:border-white/20"}`}
                    >
                      <span>{labelEstadoDropdown}</span>
                      <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ${dropdownEstadoOpen ? "rotate-180" : ""}`} />
                    </button>

                    {dropdownEstadoOpen && (
                      <div className="absolute top-full left-0 mt-1 w-44 bg-arandu-dark-light border border-white/10 rounded-lg shadow-xl z-50 py-1 overflow-hidden">
                        {/* Opción "Todos" */}
                        <button
                          onClick={() => { setFiltrosEstado(new Set()); setDropdownEstadoOpen(false); }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-all hover:bg-white/5
                            ${filtrosEstado.size === 0 ? "text-arandu-blue" : "text-slate-400"}`}
                        >
                          <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0
                            ${filtrosEstado.size === 0 ? "bg-arandu-blue border-arandu-blue" : "border-white/20"}`}>
                            {filtrosEstado.size === 0 && <span className="text-white text-[8px] font-bold">✓</span>}
                          </span>
                          Todos
                        </button>
                        <div className="border-t border-white/5 my-0.5" />
                        {ESTADOS.map(est => {
                          const checked = filtrosEstado.has(est.value);
                          const Icon = est.icon;
                          return (
                            <button
                              key={est.value}
                              onClick={() => toggleFiltroEstado(est.value)}
                              className="w-full flex items-center gap-2 px-3 py-2 text-xs transition-all hover:bg-white/5 text-slate-300"
                            >
                              <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 transition-all
                                ${checked ? "bg-arandu-blue border-arandu-blue" : "border-white/20"}`}>
                                {checked && <span className="text-white text-[8px] font-bold">✓</span>}
                              </span>
                              <Icon className="w-3 h-3 opacity-60 flex-shrink-0" />
                              {est.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Prioridad */}
                  <select value={filtroPrio} onChange={e => setFiltroPrio(e.target.value)}
                    className="flex-1 min-w-[80px] bg-arandu-dark border border-white/10 rounded-lg px-2 py-1.5 text-slate-400 text-xs focus:outline-none focus:border-arandu-blue">
                    <option value="">Prioridad</option>
                    {PRIORIDADES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>

                  {/* Toggle no leídos */}
                  <button
                    onClick={() => setSoloNoLeidos(v => !v)}
                    title={soloNoLeidos ? "Mostrar todos" : "Solo con novedades"}
                    className={`flex items-center gap-1 px-2 py-1.5 rounded-lg border text-xs transition-all flex-shrink-0
                      ${soloNoLeidos
                        ? "bg-arandu-blue/20 border-arandu-blue text-arandu-blue"
                        : "bg-arandu-dark border-white/10 text-slate-400 hover:border-white/20"}`}
                  >
                    <BellRing className="w-3.5 h-3.5" />
                    {novedadesCount > 0 && (
                      <span className={`text-[9px] font-bold px-1 py-0.5 rounded-full leading-none
                        ${soloNoLeidos ? "bg-arandu-blue text-white" : "bg-white/10 text-slate-300"}`}>
                        {novedadesCount}
                      </span>
                    )}
                  </button>

                  {seleccion.size > 0 && (
                    <>
                      <button onClick={marcarNoLeidos} title="Marcar seleccionados como no leídos"
                        className="flex items-center gap-1 px-2 py-1.5 text-[11px] text-amber-300 hover:text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg transition-all flex-shrink-0">
                        <BellRing className="w-3.5 h-3.5" /> No leído ({seleccion.size})
                      </button>
                      <button onClick={() => setSeleccion(new Set())} title="Quitar selección"
                        className="p-1.5 text-slate-400 hover:text-white bg-arandu-dark border border-white/10 rounded-lg flex-shrink-0">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                  {novedadesCount > 0 && (
                    <button onClick={marcarTodosLeidos} title="Marcar todos como leídos"
                      className="p-1.5 text-slate-400 hover:text-emerald-400 bg-arandu-dark border border-white/10 rounded-lg transition-all flex-shrink-0">
                      <CheckCheck className="w-3.5 h-3.5" />
                    </button>
                  )}

                  {/* Refrescar */}
                  <button onClick={fetchTickets} className="p-1.5 text-slate-500 hover:text-white bg-arandu-dark border border-white/10 rounded-lg transition-all flex-shrink-0" title="Refrescar">
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>

                </div>
              </>
            )}
          </div>

          {/* ── Contenido del tab ── */}
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">

            {/* Tab: Tickets */}
            {tab === "tickets" && (
              loading ? (
                <div className="text-center py-10 text-slate-500 text-sm">Cargando...</div>
              ) : ticketsFiltrados.length === 0 ? (
                <div className="text-center py-10">
                  <LifeBuoy className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">No hay tickets</p>
                  {puedeCrear && (
                    <button onClick={() => setShowForm(true)} className="mt-2 text-arandu-blue text-sm hover:underline">
                      + Crear el primero
                    </button>
                  )}
                </div>
              ) : ticketsFiltrados.map(t => {
                const isActive = ticketDetalle?.id === t.id;
                return (
                  <div key={t.id} className="relative">
                    <input type="checkbox" checked={seleccion.has(t.id)} onChange={() => toggleSel(t.id)}
                      onClick={e => e.stopPropagation()} title="Seleccionar"
                      className="absolute right-2 bottom-2 z-10 w-3.5 h-3.5 accent-amber-500 cursor-pointer" />
                  <button onClick={() => fetchDetalle(t.id)}
                    className={`w-full text-left p-3 rounded-xl border transition-all ${
                      isActive ? "border-arandu-blue/50 bg-arandu-blue/10"
                      : t.tiene_novedades ? "border-arandu-blue/20 bg-arandu-blue/5 hover:bg-arandu-blue/10"
                      : "border-white/5 bg-white/2 hover:bg-white/5 hover:border-white/10"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {t.tiene_novedades && <div className="w-2 h-2 rounded-full bg-arandu-blue flex-shrink-0 mt-0.5" />}
                        <span className="text-white text-sm font-medium truncate">{t.titulo}</span>
                      </div>
                      <PrioBadge value={t.prioridad} />
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-slate-500 flex-wrap">
                      <span className="font-mono text-slate-400">{t.numero}</span>
                      <EstadoBadge value={t.estado} />
                      {t.empresa_nombre && <span className="truncate max-w-[100px]">{t.empresa_nombre}</span>}
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-slate-600 text-[10px]">{t.creado_por_nombre}</span>
                      <span className="text-slate-600 text-[10px]">{fmtFecha(t.fecha_creacion).split(",")[0]}</span>
                    </div>
                    {t.respuestas_visibles > 0 && (
                      <div className={`mt-1 text-[10px] flex items-center gap-1 ${t.tiene_novedades ? "text-arandu-blue" : "text-emerald-500/70"}`}>
                        <MessageSquare className="w-2.5 h-2.5" />
                        {t.respuestas_visibles} respuesta{t.respuestas_visibles > 1 ? "s" : ""}
                        {t.tiene_novedades && " · Nueva"}
                      </div>
                    )}
                  </button>
                  </div>
                );
              })
            )}

            {/* Tab: Avisos */}
            {tab === "avisos" && (
              loadingAvisos ? (
                <div className="text-center py-10 text-slate-500 text-sm">Cargando avisos...</div>
              ) : avisos.length === 0 ? (
                <div className="text-center py-10">
                  <Megaphone className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">No hay avisos publicados</p>
                </div>
              ) : avisos.map(a => (
                <AvisoCard key={a.id} aviso={a} esSoporte={esSoporte}
                  empresasPropias={empresasPropias}
                  onLeer={handleLeerAviso}
                  onEditar={handleEditarAviso}
                  onEliminar={handleEliminarAviso}
                />
              ))
            )}
          </div>
        </div>

        {/* ── Panel derecho: detalle ticket ── */}
        <div className={`flex-1 flex flex-col min-h-screen ${!ticketDetalle ? "hidden lg:flex items-center justify-center" : "flex"}`}>
          {!ticketDetalle ? (
            <div className="text-center">
              <LifeBuoy className="w-16 h-16 text-slate-800 mx-auto mb-3" />
              <p className="text-slate-600">Seleccioná un ticket para ver el detalle</p>
            </div>
          ) : loadingDetalle ? (
            <div className="flex items-center justify-center flex-1">
              <div className="text-arandu-blue animate-pulse text-sm">Cargando ticket...</div>
            </div>
          ) : (
            <div className="flex flex-col h-full max-h-screen">
              {/* Header detalle */}
              <div className="sticky top-0 z-10 bg-arandu-dark/95 backdrop-blur-sm border-b border-white/5 p-4">
                <div className="flex items-start gap-3">
                  <button onClick={() => setTicketDetalle(null)}
                    className="p-1.5 text-slate-400 hover:text-white transition-all mt-0.5 flex-shrink-0">
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono text-arandu-blue text-sm">{ticketDetalle.numero}</span>
                      <EstadoBadge value={ticketDetalle.estado} />
                      <PrioBadge value={ticketDetalle.prioridad} />
                      <span className="text-slate-500 text-xs capitalize bg-white/5 px-1.5 py-0.5 rounded">{ticketDetalle.categoria}</span>
                    </div>
                    <h2 className="text-white font-heading font-bold text-base truncate">{ticketDetalle.titulo}</h2>
                    <p className="text-slate-500 text-xs mt-0.5">
                      {ticketDetalle.creado_por_nombre} · {fmtFecha(ticketDetalle.fecha_creacion)}
                      {ticketDetalle.empresa_nombre && ` · ${ticketDetalle.empresa_nombre}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                    {puedeGestionar && (
                      <>
                        <div className="flex gap-1 flex-wrap">
                          {ESTADOS.filter(e => e.value !== ticketDetalle.estado).map(e => (
                            <button key={e.value} onClick={() => handleCambiarEstado(e.value)}
                              disabled={savingEstado} title={`Marcar como ${e.label}`}
                              className={`text-[10px] px-2 py-1 rounded-lg border transition-all ${e.cls} hover:opacity-80 disabled:opacity-40`}>
                              {e.label}
                            </button>
                          ))}
                        </div>
                        <select
                          value={ticketDetalle.prioridad}
                          onChange={e => handleCambiarPrioridad(e.target.value)}
                          disabled={savingPrioridad}
                          title="Cambiar prioridad"
                          className={`text-[10px] px-2 py-1 rounded-lg border transition-all cursor-pointer focus:outline-none disabled:opacity-40 ${prioMeta(ticketDetalle.prioridad).cls}`}
                        >
                          {PRIORIDADES.map(p => (
                            <option key={p.value} value={p.value}>{p.label}</option>
                          ))}
                        </select>
                      </>
                    )}
                    {puedeEliminar && (
                      <button onClick={() => handleEliminar(ticketDetalle.id)}
                        className="p-1.5 text-slate-500 hover:text-red-400 transition-all" title="Eliminar ticket">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {(ticketDetalle.historial || []).map(entrada => (
                  <EntradaTimeline key={entrada.id} entrada={entrada}
                    esSoporte={puedeResponder}
                    onToggleVisibilidad={handleToggleVisibilidad}
                    currentUserId={user?.id}
                  />
                ))}
                {(ticketDetalle.historial || []).length === 0 && (
                  <p className="text-slate-600 text-sm text-center py-8">Sin historial aún</p>
                )}
              </div>

              {/* Formulario respuesta */}
              {ticketDetalle.estado !== "cerrado" ? (
                <div className="border-t border-white/5 bg-arandu-dark-light p-4">
                  <form onSubmit={handleResponder} className="space-y-3">
                    {puedeResponder && (
                      <div className="flex gap-1.5 flex-wrap">
                        {TIPOS_ENTRADA.filter(t => !t.soloSoporte || puedeResponder).map(t => {
                          const Icon = t.icon;
                          return (
                            <button key={t.value} type="button"
                              onClick={() => setRespForm(f => ({ ...f, tipo: t.value, visible_usuario: t.value === "respuesta" ? true : f.visible_usuario }))}
                              className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border transition-all ${
                                respForm.tipo === t.value ? "border-arandu-blue bg-arandu-blue/20 text-white" : "border-white/10 text-slate-400 hover:text-white hover:border-white/20"
                              }`}>
                              <Icon className="w-3 h-3" />{t.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="relative">
                    <textarea value={respForm.contenido} onChange={e => {
                        const v = e.target.value;
                        setRespForm(f => ({ ...f, contenido: v }));
                        const m = /(?:^|\s)@([^\s@]*)$/.exec(v.slice(0, e.target.selectionStart ?? v.length));
                        setMencionQuery(m ? m[1].toLowerCase() : null);
                      }}
                      placeholder={respForm.tipo === "proceso" ? "Describe los pasos realizados..."
                        : respForm.tipo === "nota_interna" ? "Nota interna (solo soporte)..."
                        : respForm.tipo === "respuesta" ? "Respuesta oficial para el usuario..."
                        : "Agregar comentario..."}
                      rows={3}
                      className="w-full bg-arandu-dark border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-arandu-blue resize-none"
                    />
                    {mencionQuery !== null && (() => {
                      const lista = mencionables.filter(u => ((u.name || u.email || "").toLowerCase().includes(mencionQuery))).slice(0, 8);
                      return (
                        <div className="absolute left-0 bottom-full mb-1 z-20 w-72 max-h-56 overflow-y-auto bg-arandu-dark-light border border-white/15 rounded-xl shadow-xl">
                          <p className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-slate-500">Mencionar — se notifica solo a esa persona</p>
                          {lista.length === 0 && <p className="px-3 py-2 text-xs text-slate-500">Sin coincidencias</p>}
                          {lista.map(u => (
                            <button type="button" key={u.id}
                              onClick={() => {
                                const nombre = u.name || u.email;
                                setRespForm(f => ({ ...f, contenido: f.contenido.replace(/@([^\s@]*)$/, `@${nombre} `) }));
                                setRespMenciones(prev => prev.some(x => x.id === u.id) ? prev : [...prev, { id: u.id, name: nombre }]);
                                setMencionQuery(null);
                              }}
                              className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/10 flex items-center gap-2">
                              <AtSign className="w-3.5 h-3.5 text-arandu-blue" />
                              <span className="truncate">{u.name || u.email}</span>
                              <span className="ml-auto text-[10px] text-slate-500">{u.role}</span>
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                    </div>
                    <p className="text-[11px] text-slate-500 -mt-1">Escribí <span className="text-arandu-blue">@</span> para mencionar a alguien: solo esa persona recibe la notificación.</p>
                    <ImagenAdjunta imagenes={respImagenes} setImagenes={setRespImagenes} archivos={respArchivos} setArchivos={setRespArchivos} />
                    <div className="flex items-center justify-between">
                      {puedeResponder && !["nota_interna"].includes(respForm.tipo) ? (
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <div onClick={() => setRespForm(f => ({ ...f, visible_usuario: !f.visible_usuario }))}
                            className={`w-9 h-5 rounded-full border-2 transition-all flex items-center px-0.5 ${respForm.visible_usuario ? "bg-emerald-500 border-emerald-500" : "bg-white/10 border-white/20"}`}>
                            <div className={`w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${respForm.visible_usuario ? "translate-x-4" : "translate-x-0"}`} />
                          </div>
                          <span className={`text-xs ${respForm.visible_usuario ? "text-emerald-400" : "text-slate-500"}`}>
                            {respForm.visible_usuario ? "Visible al usuario" : "Solo interno"}
                          </span>
                        </label>
                      ) : <div />}
                      <button type="submit" disabled={savingResp || !respForm.contenido.trim()}
                        className="flex items-center gap-2 bg-arandu-blue hover:bg-arandu-blue/80 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                        <Send className="w-3.5 h-3.5" />
                        {savingResp ? "Enviando..." : "Enviar"}
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <div className="border-t border-white/5 p-4 text-center text-slate-500 text-sm">
                  Ticket cerrado
                  {puedeGestionar && (
                    <button onClick={() => handleCambiarEstado("abierto")} className="ml-2 text-arandu-blue hover:underline">Reabrir</button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Modal: Nuevo ticket ── */}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-5 border-b border-white/10">
                <h2 className="font-heading text-white font-bold flex items-center gap-2">
                  <LifeBuoy className="w-5 h-5 text-arandu-blue" /> Nuevo ticket de soporte
                </h2>
                <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-white transition-all">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleCrear} className="p-5 space-y-4">
                {/* Empresa — OBLIGATORIA */}
                <div>
                  <label className="text-slate-400 text-xs block mb-1 font-semibold">
                    Empresa * <span className="text-slate-600 font-normal">(seleccioná a cuál empresa pertenece el ticket)</span>
                  </label>
                  {misEmpresas.length === 0 ? (
                    <p className="text-slate-500 text-sm border border-white/10 rounded-lg px-3 py-2">
                      No tenés empresas asignadas. Contactá al administrador.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 gap-1.5 max-h-36 overflow-y-auto pr-0.5">
                      {misEmpresas.map(ep => {
                        const sel = nuevoForm.empresa_id === String(ep.id);
                        return (
                          <button key={ep.id} type="button"
                            onClick={() => {
                              setNuevoForm(f => ({ ...f, empresa_id: String(ep.id), empresa_nombre: ep.nombre }));
                              setVisibleParaIds([]);
                              fetchEmpresaUsuarios(String(ep.id));
                            }}
                            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border text-left text-sm transition-all ${
                              sel ? "border-arandu-blue bg-arandu-blue/15 text-white" : "border-white/10 text-slate-400 hover:border-white/20 hover:text-white"
                            }`}>
                            {ep.logo ? (
                              <img src={ep.logo} alt={ep.nombre} className="w-5 h-5 object-contain rounded flex-shrink-0" />
                            ) : (
                              <div className="w-5 h-5 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                                style={{ backgroundColor: ep.color || "#64748b" }}>{ep.nombre.charAt(0)}</div>
                            )}
                            <span className="truncate">{ep.nombre}</span>
                            {sel && <CheckCircle className="w-4 h-4 text-arandu-blue ml-auto flex-shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* ── Visual para el usuario ── */}
                <div className="border border-white/10 rounded-xl p-3 space-y-2.5 bg-white/2">
                  <label className="flex items-center justify-between cursor-pointer select-none">
                    <span className="text-slate-300 text-xs font-semibold flex items-center gap-1.5">
                      <Eye className="w-3.5 h-3.5 text-emerald-400" /> Visual para el usuario
                    </span>
                    <div onClick={() => setVisibleParaActivo(v => !v)}
                      className={`w-9 h-5 rounded-full border-2 transition-all flex items-center px-0.5 cursor-pointer ${visibleParaActivo ? "bg-emerald-500 border-emerald-500" : "bg-white/10 border-white/20"}`}>
                      <div className={`w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${visibleParaActivo ? "translate-x-4" : "translate-x-0"}`} />
                    </div>
                  </label>

                  {visibleParaActivo && (
                    <div className="space-y-2">
                      {/* Todos o específicos */}
                      <div className="flex gap-2">
                        <button type="button"
                          onClick={() => setVisibleParaTodos(true)}
                          className={`flex-1 text-xs px-2.5 py-1.5 rounded-lg border transition-all ${visibleParaTodos ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-white/10 text-slate-500 hover:border-white/20 hover:text-white"}`}>
                          Todos los usuarios
                        </button>
                        <button type="button"
                          onClick={() => setVisibleParaTodos(false)}
                          className={`flex-1 text-xs px-2.5 py-1.5 rounded-lg border transition-all ${!visibleParaTodos ? "border-arandu-blue bg-arandu-blue/15 text-blue-300" : "border-white/10 text-slate-500 hover:border-white/20 hover:text-white"}`}>
                          Usuarios específicos
                        </button>
                      </div>

                      {/* Lista de usuarios (cuando no es "todos") */}
                      {!visibleParaTodos && (
                        <div className="space-y-1 max-h-32 overflow-y-auto pr-0.5">
                          {empresaUsuarios.length === 0 ? (
                            <p className="text-slate-600 text-xs py-1 text-center">
                              {nuevoForm.empresa_id ? "No hay usuarios en esta empresa" : "Seleccioná una empresa primero"}
                            </p>
                          ) : (
                            empresaUsuarios.map(u => {
                              const sel = visibleParaIds.includes(u.id);
                              return (
                                <button key={u.id} type="button"
                                  onClick={() => setVisibleParaIds(prev => sel ? prev.filter(id => id !== u.id) : [...prev, u.id])}
                                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-left text-xs transition-all ${sel ? "border-arandu-blue bg-arandu-blue/15 text-white" : "border-white/10 text-slate-400 hover:border-white/20 hover:text-white"}`}>
                                  <div className="w-5 h-5 rounded-full bg-slate-600 flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0">
                                    {(u.name || u.email || "?").charAt(0).toUpperCase()}
                                  </div>
                                  <span className="truncate">{u.name || u.email}</span>
                                  {sel && <CheckCircle className="w-3.5 h-3.5 text-arandu-blue ml-auto flex-shrink-0" />}
                                </button>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-slate-400 text-xs block mb-1">Título *</label>
                  <input type="text" value={nuevoForm.titulo} onChange={e => setNuevoForm(f => ({ ...f, titulo: e.target.value }))}
                    placeholder="Resumen breve del problema..."
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue" required />
                </div>

                <div>
                  <label className="text-slate-400 text-xs block mb-1">Descripción *</label>
                  <textarea value={nuevoForm.descripcion} onChange={e => setNuevoForm(f => ({ ...f, descripcion: e.target.value }))}
                    placeholder="Describí el problema: qué pasó, cuándo, en qué equipo..."
                    rows={4} className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue resize-none" required />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-slate-400 text-xs block mb-1">Prioridad</label>
                    <select value={nuevoForm.prioridad} onChange={e => setNuevoForm(f => ({ ...f, prioridad: e.target.value }))}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue">
                      {PRIORIDADES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-slate-400 text-xs block mb-1">Categoría</label>
                    <select value={nuevoForm.categoria} onChange={e => setNuevoForm(f => ({ ...f, categoria: e.target.value }))}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue">
                      {CATEGORIAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-slate-400 text-xs block mb-1">Adjuntos (imágenes, PDF, Word, Excel, txt)</label>
                  <ImagenAdjunta imagenes={nuevoImagenes} setImagenes={setNuevoImagenes} archivos={nuevoArchivos} setArchivos={setNuevoArchivos} />
                </div>

                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={() => {
                    setShowForm(false);
                    setVisibleParaActivo(false); setVisibleParaTodos(true);
                    setVisibleParaIds([]); setEmpresaUsuarios([]);
                  }}
                    className="flex-1 px-4 py-2 border border-white/10 rounded-lg text-slate-300 hover:text-white hover:border-white/30 transition-all text-sm">
                    Cancelar
                  </button>
                  <button type="submit" disabled={savingNuevo || !nuevoForm.empresa_id}
                    className="flex-1 bg-arandu-blue hover:bg-arandu-blue/80 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                    <Plus className="w-4 h-4" />
                    {savingNuevo ? "Creando..." : "Crear ticket"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Modal: Crear/Editar Aviso ── */}
      <AnimatePresence>
        {showAvisoForm && puedeCrearAviso && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-5 border-b border-white/10">
                <h2 className="font-heading text-white font-bold flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-violet-400" />
                  {editingAviso ? "Editar aviso" : "Publicar aviso"}
                </h2>
                <button onClick={() => setShowAvisoForm(false)} className="text-slate-400 hover:text-white transition-all">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleGuardarAviso} className="p-5 space-y-4">
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Título *</label>
                  <input type="text" value={avisoForm.titulo} onChange={e => setAvisoForm(f => ({ ...f, titulo: e.target.value }))}
                    placeholder="Título del aviso..."
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-violet-400" required />
                </div>

                <div>
                  <label className="text-slate-400 text-xs block mb-1">Contenido *</label>
                  <textarea value={avisoForm.contenido} onChange={e => setAvisoForm(f => ({ ...f, contenido: e.target.value }))}
                    placeholder="Escribí el mensaje del aviso..."
                    rows={4} className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-violet-400 resize-none" required />
                </div>

                {/* Empresa destino */}
                <div>
                  <label className="text-slate-400 text-xs block mb-2 font-semibold">Destino</label>
                  <div className="space-y-1.5">
                    <button type="button" onClick={() => toggleEmpresaAviso("all")}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                        (avisoForm.empresa_ids || []).includes("all")
                          ? "border-violet-500 bg-violet-500/15 text-violet-200"
                          : "border-white/10 text-slate-400 hover:border-white/20"
                      }`}>
                      <Building2 className="w-4 h-4" />
                      Todas las empresas
                    </button>
                    {empresasPropias.map(ep => {
                      const sel = (avisoForm.empresa_ids || []).includes(String(ep.id));
                      return (
                        <button key={ep.id} type="button" onClick={() => toggleEmpresaAviso(String(ep.id))}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                            sel ? "border-violet-500 bg-violet-500/15 text-violet-200" : "border-white/10 text-slate-400 hover:border-white/20"
                          }`}>
                          {ep.logo ? (
                            <img src={ep.logo} alt={ep.nombre} className="w-4 h-4 object-contain rounded" />
                          ) : (
                            <div className="w-4 h-4 rounded flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                              style={{ backgroundColor: ep.color || "#64748b" }}>{ep.nombre.charAt(0)}</div>
                          )}
                          {ep.nombre}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="text-slate-400 text-xs block mb-1">Fecha de expiración (opcional)</label>
                  <DateInput value={avisoForm.fecha_expiracion} onChange={e => setAvisoForm(f => ({ ...f, fecha_expiracion: e.target.value }))}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-violet-400" />
                </div>

                <div>
                  <label className="text-slate-400 text-xs block mb-1">Imágenes adjuntas</label>
                  <ImagenAdjunta imagenes={avisoImagenes} setImagenes={setAvisoImagenes} />
                </div>

                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={() => setShowAvisoForm(false)}
                    className="flex-1 px-4 py-2 border border-white/10 rounded-lg text-slate-300 hover:text-white hover:border-white/30 transition-all text-sm">
                    Cancelar
                  </button>
                  <button type="submit" disabled={savingAviso}
                    className="flex-1 bg-violet-600 hover:bg-violet-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                    <Megaphone className="w-4 h-4" />
                    {savingAviso ? "Publicando..." : editingAviso ? "Guardar cambios" : "Publicar aviso"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
